import React, { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiSearch } from "react-icons/fi";
import Logo from "../../assets/images/DocuVerusLogoorg.png";
import { useNotify } from "react-admin";
import { ATS_API_URL, SOFTWARE_VERSION } from "../../config";
import { completeLogin } from "../../authProviders/completeLogin";
import {
  getAtsStatusDisplayLabel,
  getDocUploadStatusDisplayLabel,
} from "../../utils/atsApplicationStatus";
import { getScreenedStatusStyle, getStatusStyle } from "../../utils/statusStyles";
import MfaLogin from "./MfaLogin";
import "../../assets/styles/style.css";
import "./loginPrototype.css";

type AuthStep =
  | "email"
  | "password"
  | "mfa"
  | "otp-choice"
  | "otp-verify"
  | "role-picker";
type PrimaryAuthType = "password" | "otp" | null;
type OtpChannel = "email" | "sms";
type OtpContext = "verification" | "mfa" | null;
type StatusTone = "good" | "hint";
type MfaMode = "legacy" | "session" | null;

type RoleOption = {
  id: string;
  label: string;
  description?: string;
  roleCategory?: string;
  userrole?: string;
  lastLoginAt?: string | null;
  applicationInviteType?: string | null;
  applicationStatus?: string | null;
  screeningDetails?: string | null;
  propertyOrOfficeName?: string | null;
  subscriberName?: string | null;
  applicantId?: string | number | null;
};

type RoleGroup = {
  key: string;
  label: string;
  roles: RoleOption[];
};

type LookupResponse = {
  sessionId: string;
  authType: Exclude<PrimaryAuthType, null>;
  passwordSecondaryOption?: boolean;
  channels?: OtpChannel[];
  roles?: RoleOption[];
};

type TransitionResponse = {
  nextStep: "mfa" | "role-picker" | "complete";
  sessionId?: string;
  username?: string;
  userId?: string;
  channels?: OtpChannel[];
  roles?: RoleOption[];
  data?: Record<string, any>;
};

type PersistedEmailFirstState = {
  savedAt: number;
  step: AuthStep;
  sessionId: string;
  email: string;
  channels: OtpChannel[];
  roles: RoleOption[];
  primaryAuthType: PrimaryAuthType;
  passwordSecondaryOption: boolean;
  otpContext: OtpContext;
  selectedOtpChannel: OtpChannel | null;
  sentChannels: OtpChannel[];
  rolePickerReturnStep: AuthStep;
  selectedRoleTab: string;
  mfaMode: MfaMode;
  mfaUsername: string;
  mfaUserId: string;
  statusMessage: string;
  statusTone: StatusTone;
  roleSearchTerm: string;
  selectedRoleId: string;
};

const initialFormValues = {
  email: "",
  password: "",
  otp: "",
};

const getApplicationStatusStyle = (status?: string | null, screeningDetails?: string | null) => {
  const displayStatus = getAtsStatusDisplayLabel(status || undefined);

  // Dashboard colours Screened according to the screening outcome, for both
  // ATS and Doc Upload applications.
  if (displayStatus === "Screened") {
    return getScreenedStatusStyle(screeningDetails);
  }

  return getStatusStyle(status || undefined);
};

const getRoleApplicationStatusLabel = (role: RoleOption) => {
  const invitationType = String(role.applicationInviteType || "").trim().toLowerCase();
  const isDocUpload = invitationType === "doc upload" || /^vr\b/i.test(role.label || "");

  return isDocUpload
    ? getDocUploadStatusDisplayLabel(role.applicationStatus || undefined)
    : getAtsStatusDisplayLabel(role.applicationStatus || undefined);
};

const notifyOptions = {
  anchorOrigin: { vertical: "top", horizontal: "right" } as const,
};

const EMAIL_FIRST_SESSION_STORAGE_KEY = "docuverus.emailFirstAuthState";
const EMAIL_FIRST_SESSION_TTL_MS = 15 * 60 * 1000;

const getRoleGroupLabel = (role: RoleOption) =>
  role.userrole || role.description || role.label || "Role";

const getRolePriorityGroup = (role: RoleOption) => {
  const roleName = String(role.userrole || role.description || "").trim();
  const normalizedRoleName = roleName.toLowerCase();
  const displayName = String(role.label || "").toLowerCase();

  if (normalizedRoleName === "docuverus super admin") {
    return { key: "docuverus-super-admin", label: "Docuverus Super Admin", priority: 1 };
  }

  if (
    normalizedRoleName.includes("docuverus") ||
    displayName.includes("system workspace")
  ) {
    return { key: "docuverus-custom-roles", label: "Docuverus Custom Roles", priority: 2 };
  }

  if (["admin", "manager", "agent", "super admin"].includes(normalizedRoleName)) {
    return { key: "subscriber-roles", label: "Subscriber Roles", priority: 3 };
  }

  if (role.roleCategory === "staff") {
    return { key: "subscriber-custom-roles", label: "Subscriber Custom Roles", priority: 4 };
  }

  if (role.roleCategory === "applicant_like") {
    return { key: "applicant-roles", label: "Applicant Roles", priority: 5 };
  }

  return { key: "landlord-third-party-roles", label: "Landlord & Third Party Roles", priority: 6 };
};

const getRoleGroupDomId = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "role";

export const deduplicateRoleOptions = (roleOptions: RoleOption[]) => {
  const seenRoleKeys = new Set<string>();

  return roleOptions.filter((role) => {
    const isApplicantRole = role.roleCategory === "applicant_like";
    const normalizedInviteType = String(role.applicationInviteType || "")
      .trim()
      .toLowerCase();
    const normalizedLabel = String(role.label || "").trim().toLowerCase();
    const applicationId =
      normalizedLabel.match(/application\s+([a-z0-9-]+)/i)?.[1] ||
      normalizedLabel;
    const participantId = String(role.applicantId || "").trim().toLowerCase();
    const inviteType =
      normalizedInviteType === "doc upload" || normalizedLabel.startsWith("vr")
        ? "vr"
        : normalizedInviteType === "guest card" || normalizedLabel.startsWith("guest card")
          ? "guest-card"
          : "ats";
    const key = isApplicantRole
      ? [
          "applicant",
          inviteType,
          applicationId,
          participantId || String(role.userrole || role.description || "").trim().toLowerCase(),
        ].join(":")
      : `role:${role.id}`;

    if (seenRoleKeys.has(key)) {
      return false;
    }

    seenRoleKeys.add(key);
    return true;
  });
};

const groupRolesByUserRole = (roleOptions: RoleOption[]): RoleGroup[] => {
  const groupedRoles = new Map<string, RoleGroup & { priority: number }>();

  deduplicateRoleOptions(roleOptions).forEach((role) => {
    const roleGroup = getRolePriorityGroup(role);
    const key = roleGroup.key;
    const currentGroup = groupedRoles.get(key);

    if (currentGroup) {
      currentGroup.roles.push(role);
      return;
    }

    groupedRoles.set(key, {
      key,
      label: roleGroup.label,
      roles: [role],
      priority: roleGroup.priority,
    });
  });

  return Array.from(groupedRoles.values())
    .sort((left, right) => left.priority - right.priority)
    .map(({ priority, ...group }) => group);
};

const getRoleCardTitle = (role: RoleOption) => {
  const label = String(role.label || "").trim();

  if (role.roleCategory === "landlord_like") {
    return role.userrole || role.description || "Landlord Third Party Agent";
  }

  if (role.roleCategory !== "applicant_like" || !label) {
    return (
      label.replace(/\s*-\s*system workspace$/i, "") ||
      role.userrole ||
      role.description ||
      "Role access"
    );
  }

  const applicationLabel = label.match(/application\b.*$/i)?.[0] || label;
  const invitationType = String(role.applicationInviteType || "").trim().toLowerCase();
  const prefix = invitationType === "doc upload" || /^vr\b/i.test(label)
    ? "VR"
    : invitationType === "guest card" || /^guest card\b/i.test(label)
      ? "Guest Card"
      : "ATS";

  return `${prefix} - ${applicationLabel}`;
};

const getRoleCardLastLogin = (role: RoleOption) => {
  const rawTimestamp = String(role.lastLoginAt || "").trim();
  if (!rawTimestamp) {
    return "No login recorded";
  }

  // New API responses are ISO UTC timestamps.  The fallback also supports
  // previously returned SQL timestamps, which are stored in UTC.
  const normalizedTimestamp = /[zZ]|[+-]\d\d:\d\d$/.test(rawTimestamp)
    ? rawTimestamp
    : rawTimestamp.replace(" ", "T") + "Z";
  const date = new Date(normalizedTimestamp);

  if (Number.isNaN(date.getTime())) {
    return rawTimestamp;
  }

  const localDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  const localTime = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${localDate} ${localTime}`;
};

const getRoleCardSummaryLine = (role: RoleOption) => {
  if (role.roleCategory === "applicant_like") {
    return [role.userrole, role.propertyOrOfficeName].filter(Boolean).join(" - ");
  }

  if (role.roleCategory === "staff" || role.roleCategory === "landlord_like") {
    return "Last Login";
  }

  return role.description || role.userrole || "Role access";
};

const getRoleCardDetailLine = (role: RoleOption) => {
  if (role.roleCategory === "applicant_like") {
    return role.subscriberName || role.applicationStatus || "";
  }

  if (role.roleCategory === "staff" || role.roleCategory === "landlord_like") {
    return getRoleCardLastLogin(role);
  }

  return role.description || role.userrole || "";
};

const getRoleSelectionMessage = (role: RoleOption | null) => {
  if (!role) {
    return "Select an access role to continue.";
  }

  return "Access selected. Continue to proceed.";
};

const getRoleSearchText = (role: RoleOption) =>
  [
    getRoleCardTitle(role),
    role.userrole,
    role.description,
    role.applicationStatus,
    role.propertyOrOfficeName,
    role.subscriberName,
    role.lastLoginAt,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const clearPersistedEmailFirstState = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(EMAIL_FIRST_SESSION_STORAGE_KEY);
};

const readPersistedEmailFirstState = (): PersistedEmailFirstState | null => {
  if (typeof window === "undefined") return null;

  const rawState = window.sessionStorage.getItem(EMAIL_FIRST_SESSION_STORAGE_KEY);
  if (!rawState) {
    return null;
  }

  try {
    const parsedState = JSON.parse(rawState) as PersistedEmailFirstState;
    if (
      !parsedState?.sessionId ||
      !parsedState?.step ||
      !parsedState?.savedAt ||
      Date.now() - Number(parsedState.savedAt) > EMAIL_FIRST_SESSION_TTL_MS
    ) {
      clearPersistedEmailFirstState();
      return null;
    }

    return parsedState;
  } catch (_error) {
    clearPersistedEmailFirstState();
    return null;
  }
};

const isAuthenticationSessionExpiredMessage = (message: string) =>
  /authentication session expired/i.test(String(message || ""));

const Login = () => {
  const notify = useNotify();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<AuthStep>("email");
  const [sessionId, setSessionId] = useState("");
  const [channels, setChannels] = useState<OtpChannel[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [primaryAuthType, setPrimaryAuthType] =
    useState<PrimaryAuthType>(null);
  const [passwordSecondaryOption, setPasswordSecondaryOption] =
    useState(false);
  const [otpContext, setOtpContext] = useState<OtpContext>(null);
  const [selectedOtpChannel, setSelectedOtpChannel] =
    useState<OtpChannel | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState<StatusTone>("hint");
  const [sentChannels, setSentChannels] = useState<OtpChannel[]>([]);
  const [rolePickerReturnStep, setRolePickerReturnStep] =
    useState<AuthStep>("email");
  const [selectedRoleTab, setSelectedRoleTab] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [mfaMode, setMfaMode] = useState<MfaMode>(null);
  const [mfaUsername, setMfaUsername] = useState("");
  const [mfaUserId, setMfaUserId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingLogin, setIsCheckingLogin] = useState(true);

  useEffect(() => {
    document.title = "Docuverus";

    const auth = localStorage.getItem("auth");
    if (auth) {
      try {
        const parsedAuth = JSON.parse(auth);
        if (parsedAuth?.data) {
          clearPersistedEmailFirstState();
          // Reuse the same role-aware redirect used immediately after login.
          // A Doc Upload applicant must return to its VR invitation route, not
          // be forced into the ATS applicant application when using Back.
          const loginResult = completeLogin(parsedAuth, notify);
          if (typeof loginResult?.redirectTo === "string") {
            window.location.replace(loginResult.redirectTo);
          }
          return;
        }
      } catch (_error) {
        localStorage.removeItem("auth");
      }
    }

    const persistedState = readPersistedEmailFirstState();
    if (persistedState) {
      setStep(persistedState.step);
      setSessionId(persistedState.sessionId);
      setChannels(persistedState.channels || []);
      setRoles(deduplicateRoleOptions(persistedState.roles || []));
      setPrimaryAuthType(persistedState.primaryAuthType || null);
      setPasswordSecondaryOption(Boolean(persistedState.passwordSecondaryOption));
      setOtpContext(persistedState.otpContext || null);
      setSelectedOtpChannel(persistedState.selectedOtpChannel || null);
      setSentChannels(persistedState.sentChannels || []);
      setRolePickerReturnStep(persistedState.rolePickerReturnStep || "email");
      setSelectedRoleTab(persistedState.selectedRoleTab || "");
      setSelectedRoleId(persistedState.selectedRoleId || "");
      setRoleSearchTerm(persistedState.roleSearchTerm || "");
      setMfaMode(persistedState.mfaMode || null);
      setMfaUsername(persistedState.mfaUsername || "");
      setMfaUserId(persistedState.mfaUserId || "");
      setStatusMessage(persistedState.statusMessage || "");
      setStatusTone(persistedState.statusTone === "good" ? "good" : "hint");
      setFormValues((currentValues) => ({
        ...currentValues,
        email: persistedState.email || "",
        password: "",
        otp: "",
      }));
    }

    setIsCheckingLogin(false);
  }, []);

  useEffect(() => {
    if (isCheckingLogin) {
      return;
    }

    if (step === "email" || !sessionId) {
      clearPersistedEmailFirstState();
      return;
    }

    const persistedState: PersistedEmailFirstState = {
      savedAt: Date.now(),
      step,
      sessionId,
      email: formValues.email,
      channels,
      roles,
      primaryAuthType,
      passwordSecondaryOption,
      otpContext,
      selectedOtpChannel,
      sentChannels,
      rolePickerReturnStep,
      selectedRoleTab,
      selectedRoleId,
      roleSearchTerm,
      mfaMode,
      mfaUsername,
      mfaUserId,
      statusMessage,
      statusTone,
    };

    window.sessionStorage.setItem(
      EMAIL_FIRST_SESSION_STORAGE_KEY,
      JSON.stringify(persistedState)
    );
  }, [
    isCheckingLogin,
    step,
    sessionId,
    formValues.email,
    channels,
    roles,
    primaryAuthType,
    passwordSecondaryOption,
    otpContext,
    selectedOtpChannel,
    sentChannels,
    rolePickerReturnStep,
    selectedRoleTab,
    selectedRoleId,
    roleSearchTerm,
    mfaMode,
    mfaUsername,
    mfaUserId,
    statusMessage,
    statusTone,
  ]);

  const clearError = (field: string) => {
    setErrors((currentErrors) => {
      if (!currentErrors[field]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const setFieldValue = (
    field: keyof typeof initialFormValues,
    value: string
  ) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    clearError(field);
  };

  const requireField = (
    field: keyof typeof initialFormValues,
    message: string
  ) => {
    const value = String(formValues[field] || "").trim();
    if (value) {
      clearError(field);
      return value;
    }

    setErrors((currentErrors) => ({
      ...currentErrors,
      [field]: message,
    }));
    return null;
  };

  const notifyError = (message: string) => {
    notify(message, {
      type: "error",
      ...notifyOptions,
    });
  };

  const notifySuccess = (message: string) => {
    notify(message, {
      type: "success",
      ...notifyOptions,
    });
  };

  const postAuthRequest = async (
    path: string,
    payload: Record<string, any>
  ) => {
    const response = await fetch(`${ATS_API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    let responseBody: any = {};
    try {
      responseBody = await response.json();
    } catch (_error) {
      responseBody = {};
    }

    if (response.status === 429) {
      const retryAfterSeconds = Number(response.headers.get("Retry-After"));
      const fallbackMessage = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? `Please wait ${Math.ceil(retryAfterSeconds / 60)} minute${Math.ceil(retryAfterSeconds / 60) === 1 ? "" : "s"} before trying again.`
        : "Too many requests. Please try again later.";
      throw new Error(responseBody?.message || fallbackMessage);
    }

    if (!response.ok) {
      const errorMessage =
        responseBody?.message || "Unable to complete request.";
      const requestError = new Error(errorMessage) as Error & {
        statusCode?: number;
      };
      requestError.statusCode = response.status;
      throw requestError;
    }

    return responseBody?.data;
  };

  const resetExpiredRoleSelection = (error: {
    message?: string;
  }) => {
    if (!isAuthenticationSessionExpiredMessage(error?.message || "")) {
      return false;
    }

    resetStepState();
    return true;
  };

  const getRequiredSessionId = () => {
    const activeSessionId = sessionId || readPersistedEmailFirstState()?.sessionId || "";
    if (activeSessionId) {
      return activeSessionId;
    }

    clearPersistedEmailFirstState();
    throw new Error("Authentication session expired. Please start again.");
  };

  const clearStatus = () => {
    setStatusMessage("");
    setStatusTone("hint");
  };

  const resetStepState = () => {
    clearPersistedEmailFirstState();
    clearStatus();
    setSentChannels([]);
    setChannels([]);
    setRoles([]);
    setSessionId("");
    setPasswordSecondaryOption(false);
    setPrimaryAuthType(null);
    setOtpContext(null);
    setSelectedOtpChannel(null);
    setRolePickerReturnStep("email");
    setSelectedRoleTab("");
    setSelectedRoleId("");
    setRoleSearchTerm("");
    setMfaMode(null);
    setMfaUsername("");
    setMfaUserId("");
    setErrors({});
    setFormValues(initialFormValues);
    setStep("email");
  };

  const finishLogin = (authData: Record<string, any>) => {
    clearPersistedEmailFirstState();
    const loginResult = completeLogin({ data: authData }, notify);
    if (typeof loginResult?.redirectTo === "string") {
      window.location.replace(loginResult.redirectTo);
    }
  };

  const moveToRolePicker = (
    nextRoles: RoleOption[] = [],
    returnStep: AuthStep
  ) => {
    const uniqueRoles = deduplicateRoleOptions(nextRoles);
    const roleGroups = groupRolesByUserRole(uniqueRoles);
    const singleRoleId = uniqueRoles.length === 1 ? uniqueRoles[0]?.id || "" : "";
    setRoles(uniqueRoles);
    setRolePickerReturnStep(returnStep);
    setSelectedRoleTab(roleGroups[0]?.key || "");
    setSelectedRoleId(singleRoleId);
    setRoleSearchTerm("");
    clearStatus();
    setStep("role-picker");
  };

  const handleTransition = async (
    transition: TransitionResponse,
    currentStep: AuthStep
  ) => {
    if (transition?.sessionId) {
      setSessionId(transition.sessionId);
    }

    if (transition?.nextStep === "mfa") {
      setChannels((transition.channels || []) as OtpChannel[]);
      setMfaMode("session");
      setMfaUsername(transition.username || "");
      setMfaUserId(transition.userId || "");
      clearStatus();
      setStep("mfa");
      return;
    }

    if (transition?.nextStep === "role-picker") {
      moveToRolePicker(transition.roles || [], currentStep);
      return;
    }

    if (transition?.nextStep === "complete" && transition?.data) {
      finishLogin(transition.data);
    }
  };

  const handleLookup = async () => {
    const email = requireField("email", "Email is required.");
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        email: "Enter a valid email address.",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const lookupResult = (await postAuthRequest(
        "/account/email-first/lookup",
        {
          email: email.toLowerCase(),
        }
      )) as LookupResponse;

      setSessionId(lookupResult.sessionId);
      setPrimaryAuthType(lookupResult.authType);
      setPasswordSecondaryOption(Boolean(lookupResult.passwordSecondaryOption));
      setChannels((lookupResult.channels || []) as OtpChannel[]);
      setRoles(deduplicateRoleOptions(lookupResult.roles || []));
      setSentChannels([]);
      setOtpContext(
        lookupResult.authType === "otp" ? "verification" : null
      );
      setSelectedOtpChannel(null);
      setErrors({});
      clearStatus();
      setFormValues((currentValues) => ({
        ...currentValues,
        password: "",
        otp: "",
      }));

      if (lookupResult.authType === "password") {
        setStep("password");
      } else {
        setMfaMode("session");
        setMfaUsername("");
        setStep("mfa");
      }
    } catch (error: any) {
      notifyError(error?.message || "Unable to continue with this email.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    const password = requireField("password", "Password is required.");
    if (!password) return;

    setIsLoading(true);
    try {
      const activeSessionId = getRequiredSessionId();
      const transition = (await postAuthRequest(
        "/account/email-first/password",
        {
          sessionId: activeSessionId,
          password,
        }
      )) as TransitionResponse;

      await handleTransition(transition, "password");
    } catch (error: any) {
      const errorMessage = error?.message || "Unable to login with password.";
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (delivery: OtpChannel) => {
    setIsLoading(true);
    try {
      const activeSessionId = getRequiredSessionId();
      await postAuthRequest("/account/email-first/send-otp", {
        sessionId: activeSessionId,
        delivery,
      });

      setSelectedOtpChannel(delivery);
      setSentChannels((currentChannels) =>
        currentChannels.includes(delivery)
          ? currentChannels
          : [...currentChannels, delivery]
      );
      setStatusMessage(
        delivery === "sms"
          ? "Verification code sent via text message. Carrier fees may apply."
          : "Verification code sent via email."
      );
      setStatusTone("good");
      setFormValues((currentValues) => ({
        ...currentValues,
        otp: "",
      }));
      clearError("otp");
      setStep("otp-verify");
      notifySuccess("Verification code sent successfully.");
    } catch (error: any) {
      const errorMessage =
        error?.message || "Unable to send verification code.";
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otp = requireField("otp", "Verification code is required.");
    if (!otp) return;

    setIsLoading(true);
    try {
      const activeSessionId = getRequiredSessionId();
      const transition = (await postAuthRequest(
        "/account/email-first/verify-otp",
        {
          sessionId: activeSessionId,
          otp,
        }
      )) as TransitionResponse;

      await handleTransition(transition, "otp-verify");
    } catch (error: any) {
      const errorMessage = error?.message || "The verification code could not be verified.";
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRole = async (roleId: string) => {
    setIsLoading(true);
    try {
      const activeSessionId = getRequiredSessionId();
      const transition = (await postAuthRequest(
        "/account/email-first/select-role",
        {
          sessionId: activeSessionId,
          roleId,
        }
      )) as TransitionResponse;

      await handleTransition(transition, rolePickerReturnStep);
    } catch (error: any) {
      const errorMessage =
        error?.message || "Unable to continue with the selected role.";
      const returnedToLogin = resetExpiredRoleSelection(error || {});
      notifyError(
        returnedToLogin
          ? "Your session has expired. Please sign in again."
          : errorMessage
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithSelectedRole = async () => {
    if (!selectedRoleId) {
      return;
    }

    await handleSelectRole(selectedRoleId);
  };

  const sendEmailFirstOtp = async (delivery: OtpChannel) => {
    try {
      const activeSessionId = getRequiredSessionId();
      const otpResponse = await postAuthRequest("/account/email-first/send-otp", {
        sessionId: activeSessionId,
        delivery,
      });

      return {
        message:
          otpResponse?.message ||
          (delivery === "sms"
            ? "Verification code sent via text message. Carrier fees may apply."
            : `Verification code sent to ${formValues.email}.`),
        successMessage: "Verification code sent successfully.",
      };
    } catch (error: any) {
      throw error;
    }
  };

  const verifyEmailFirstOtp = async (otp: string | number) => {
    try {
      const activeSessionId = getRequiredSessionId();
      const transition = (await postAuthRequest("/account/email-first/verify-otp", {
        sessionId: activeSessionId,
        otp: String(otp),
      })) as TransitionResponse;

      await handleTransition(transition, "mfa");
    } catch (error: any) {
      throw error;
    }
  };

  const handleBack = () => {
    clearStatus();

    if (step === "password") {
      if (primaryAuthType === "otp") {
        setStep("otp-choice");
        return;
      }
      setStep("email");
      return;
    }

    if (step === "otp-choice") {
      if (otpContext === "mfa" || primaryAuthType === "password") {
        setStep("password");
        return;
      }
      setStep("email");
      return;
    }

    if (step === "otp-verify") {
      setStep("otp-choice");
      return;
    }

    if (step === "role-picker") {
      setStep(rolePickerReturnStep);
    }
  };

  const renderReadonlyEmailField = (
    label = "Email",
    isRequired = false,
    wrapperClass = "",
    wrapperTestId?: string
  ) => (
    <div
      className={`email-first-field ${wrapperClass}`.trim()}
      data-testid={wrapperTestId}
    >
      <label className="email-first-label" htmlFor="login-email-readonly">
        {label}
        {isRequired ? <span> &#42;</span> : null}
      </label>
      <input
        id="login-email-readonly"
        type="email"
        disabled
        value={formValues.email}
        className="email-first-input email-first-input--readonly"
      />
    </div>
  );

  const renderStatusMessage = () =>
    statusMessage ? (
      <div
        className={`email-first-callout ${
          statusTone === "good"
            ? "email-first-callout--good"
            : "email-first-callout--hint"
        }`}
      >
        {statusMessage}
      </div>
    ) : null;

  const renderInitialStep = () => (
    <>
      <form noValidate onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void handleLookup();
      }}>
        <div className="email-first-field">
          <label className="email-first-label" htmlFor="login-email">
            Email <span>&#42;</span>
          </label>
          <input
            id="login-email"
            type="text"
            inputMode="email"
            value={formValues.email}
            placeholder="Enter Email here"
            className={`email-first-input ${errors.email ? "is-invalid" : ""}`}
            onChange={(event) => setFieldValue("email", event.target.value)}
            autoComplete="email"
            disabled={isLoading}
          />
          {errors.email ? (
            <div className="email-first-error">{errors.email}</div>
          ) : null}
        </div>
        <div className="email-first-actions email-first-actions--center">
          <button
            id="login"
            type="submit"
            className="l-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="email-first-button-loading" aria-label="Loading">
                <span className="email-first-button-spinner" aria-hidden="true" />
              </span>
            ) : "Continue"}
          </button>
        </div>
      </form>
    </>
  );

  const renderPasswordStep = () => (
    <>
      {renderStatusMessage()}
      <form
        className="email-first-password-form"
        data-testid="password-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          void handlePasswordLogin();
        }}
      >
        {renderReadonlyEmailField(
          "Email",
          true,
          "form-group cus-input-group mr-b20",
          "password-email-field"
        )}
        <div
          className="email-first-field form-group cus-input-group mr-b20"
          data-testid="password-field"
        >
          <label className="email-first-label" htmlFor="login-password">
            Password <span>&#42;</span>
          </label>
          <input
            id="login-password"
            type="password"
            value={formValues.password}
            placeholder="Enter Password here"
            className={`email-first-input ${
              errors.password ? "is-invalid" : ""
            }`}
            onChange={(event) => setFieldValue("password", event.target.value)}
            autoComplete="current-password"
          />
          {errors.password ? (
            <div className="email-first-error">{errors.password}</div>
          ) : null}
        </div>
        <div
          className="ForgetPassword email-first-forgot-password-right"
          data-testid="forgot-password-container"
        >
          <Link to="/atss/ForgotPassword" className="email-first-password-link">
            Forgot Password?
          </Link>
        </div>
        <div className="d-flex justify-content-center align-items-center email-first-actions email-first-actions--password">
          <button
            id="login"
            type="submit"
            className="l-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="email-first-button-loading" aria-label="Loading">
                <span className="email-first-button-spinner" aria-hidden="true" />
              </span>
            ) : "Login"}
          </button>
        </div>
      </form>
      {passwordSecondaryOption && primaryAuthType === "otp" ? (
        <div className="email-first-actions email-first-actions--stacked">
          <button
            type="button"
            className="email-first-ghost-btn"
            disabled={isLoading}
            onClick={() => {
              clearStatus();
              setMfaMode("session");
              setMfaUsername("");
              setStep("mfa");
            }}
          >
            Use verification code instead
          </button>
        </div>
      ) : null}
      <div className="email-first-actions email-first-actions--stacked">
        <button
          type="button"
          className="email-first-ghost-btn"
          disabled={isLoading}
          onClick={resetStepState}
        >
          Use a different email address
        </button>
      </div>
    </>
  );

  const renderOtpChoiceStep = () => (
    <>
      <div className="email-first-topbar email-first-topbar--actions-only">
        <button
          type="button"
          className="email-first-ghost-btn"
          onClick={handleBack}
        >
          Back
        </button>
      </div>
      <h2 className="email-first-title">Send Verification Code</h2>
      <p className="email-first-description">
        {otpContext === "mfa"
          ? "Choose where to receive your multi-factor verification code."
          : "Choose where to receive your verification code. Carrier fees may apply with text messages."}
      </p>
      {renderStatusMessage()}
      {renderReadonlyEmailField()}
      <div className="email-first-choice-grid">
        {channels.includes("email") ? (
          <button
            type="button"
            className="email-first-choice-card"
            onClick={() => void handleSendOtp("email")}
            disabled={isLoading}
          >
            <strong>
              {sentChannels.includes("email")
                ? "Resend code via email"
                : "Send code via email"}
            </strong>
            <span>
              Send a one-time code to the email address on this account.
            </span>
          </button>
        ) : null}
        {channels.includes("sms") ? (
          <button
            type="button"
            className="email-first-choice-card"
            onClick={() => void handleSendOtp("sms")}
            disabled={isLoading}
          >
            <strong>
              {sentChannels.includes("sms")
                ? "Resend code via text message"
                : "Send code via text message"}
            </strong>
            <span>
              Send a one-time code to the mobile number already stored in the
              current system.
            </span>
          </button>
        ) : null}
      </div>
      <div className="email-first-actions email-first-actions--stacked">
        <button
          type="button"
          className="email-first-ghost-btn"
          disabled={isLoading}
          onClick={resetStepState}
        >
          Use a different email address
        </button>
      </div>
    </>
  );

  const renderOtpVerifyStep = () => (
    <>
      <div className="email-first-topbar email-first-topbar--actions-only">
        <button
          type="button"
          className="email-first-ghost-btn"
          onClick={handleBack}
        >
          Back
        </button>
      </div>
      <h2 className="email-first-title">Verify Code</h2>
      <p className="email-first-description">
        Enter the verification code sent via{" "}
        {selectedOtpChannel === "sms" ? "text message" : "email"} to continue.
      </p>
      {renderStatusMessage()}
      <form onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void handleVerifyOtp();
      }}>
        {renderReadonlyEmailField()}
        <div className="email-first-field">
          <label className="email-first-label" htmlFor="login-otp">
            Verification Code <span>&#42;</span>
          </label>
          <input
            id="login-otp"
            type="password"
            value={formValues.otp}
            placeholder="Enter Verification Code"
            className={`email-first-input ${errors.otp ? "is-invalid" : ""}`}
            onChange={(event) => setFieldValue("otp", event.target.value)}
            autoComplete="one-time-code"
          />
          {errors.otp ? (
            <div className="email-first-error">{errors.otp}</div>
          ) : null}
        </div>
        <div className="email-first-actions">
          <button
            id="login"
            type="submit"
            className="l-btn"
            disabled={isLoading}
          >
            Verify &amp; Proceed
          </button>
          <button
            type="button"
            className="email-first-secondary-btn"
            disabled={isLoading || !selectedOtpChannel}
            onClick={() => {
              if (selectedOtpChannel) {
                void handleSendOtp(selectedOtpChannel);
              }
            }}
          >
            Resend
          </button>
        </div>
      </form>
    </>
  );

  const renderRolePickerStep = () => {
    const roleGroups = groupRolesByUserRole(roles);
    const activeRoleGroup =
      roleGroups.find((group) => group.key === selectedRoleTab) ||
      roleGroups[0] ||
      null;
    const normalizedSearchTerm = String(roleSearchTerm || "").trim().toLowerCase();
    const visibleRoles = (activeRoleGroup?.roles || []).filter((role) => {
      if (!normalizedSearchTerm) {
        return true;
      }

      return getRoleSearchText(role).includes(normalizedSearchTerm);
    });
    const hasSelectedVisibleRole = visibleRoles.some(
      (role) => role.id === selectedRoleId
    );
    const selectedRole = roles.find((role) => role.id === selectedRoleId) || null;
    const activePanelId = activeRoleGroup
      ? `role-panel-${getRoleGroupDomId(activeRoleGroup.key)}`
      : undefined;
    const activeTabId = activeRoleGroup
      ? `role-tab-${getRoleGroupDomId(activeRoleGroup.key)}`
      : undefined;

    return (
      <>
        <div className="email-first-role-selector-header">
          <div className="email-first-role-selector-copy">
            <p className="email-first-description">
              Select the role you want to enter with this email address.
            </p>
          </div>
          <span className="email-first-role-selector-count">
            {`${visibleRoles.length} available`}
          </span>
        </div>
        {roleGroups.length ? (
          <div
            className="email-first-role-tabs email-first-role-tabs--mobile-scroll"
            data-testid="role-picker-tabs"
            role="tablist"
            aria-label="Available roles"
          >
            {roleGroups.map((group) => {
              const isActive = group.key === activeRoleGroup?.key;
              const roleTabId = `role-tab-${getRoleGroupDomId(group.key)}`;
              const rolePanelId = `role-panel-${getRoleGroupDomId(group.key)}`;

              return (
                <button
                  key={group.key}
                  type="button"
                  id={roleTabId}
                  role="tab"
                  className={`email-first-role-tab ${
                    isActive ? "is-active" : ""
                  }`.trim()}
                  aria-selected={isActive}
                  aria-controls={rolePanelId}
                  tabIndex={isActive ? 0 : -1}
                  disabled={isLoading}
                  onClick={() => {
                    setSelectedRoleTab(group.key);
                    setSelectedRoleId("");
                  }}
                >
                  {group.label}
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="email-first-role-selector-divider" />
        <label
          className="email-first-role-search email-first-role-search--mobile-friendly"
          data-testid="role-picker-search"
          htmlFor="role-search"
        >
          <FiSearch className="email-first-role-search__icon" aria-hidden="true" />
          <input
            id="role-search"
            type="text"
            value={roleSearchTerm}
            placeholder="Search applications or organisations"
            className="email-first-role-search__input"
            onChange={(event) => setRoleSearchTerm(event.target.value)}
            disabled={isLoading}
          />
        </label>
        <div
          className="email-first-role-selector-list email-first-role-selector-list--mobile-scroll"
          data-testid="role-picker-grid"
          role={roleGroups.length > 1 ? "tabpanel" : undefined}
          id={activePanelId}
          aria-labelledby={activeTabId}
        >
          {visibleRoles.length ? (
            visibleRoles.map((role) => {
              const isSelected = selectedRoleId === role.id;

              return (
                <button
                  key={role.id}
                  type="button"
                  className={`email-first-role-card email-first-role-selector-card ${
                    isSelected ? "is-selected" : ""
                  }`.trim()}
                  disabled={isLoading}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  <div className="email-first-role-selector-card__content">
                    <div className="email-first-role-selector-card__heading">
                      <strong>{getRoleCardTitle(role)}</strong>
                      {role.applicationStatus ? (
                        <span
                          className="email-first-role-selector-card__status"
                          style={getApplicationStatusStyle(role.applicationStatus, role.screeningDetails)}
                        >
                          {getRoleApplicationStatusLabel(role)}
                        </span>
                      ) : null}
                    </div>
                    {role.roleCategory === "applicant_like" ? (
                      <div className="email-first-role-selector-card__details">
                        {role.userrole ? <span>{role.userrole}</span> : null}
                        {role.propertyOrOfficeName ? (
                          <span>
                            <b>Property / Office:</b> {role.propertyOrOfficeName}
                          </span>
                        ) : null}
                        {role.subscriberName ? (
                          <span>
                            <b>Subscriber:</b> {role.subscriberName}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <span className="email-first-role-card__meta-label">
                          {getRoleCardSummaryLine(role)}
                        </span>
                        {getRoleCardDetailLine(role) ? (
                          <span className="email-first-role-card__meta-value">
                            {getRoleCardDetailLine(role)}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                  <span
                    className={`email-first-role-selector-indicator ${
                      isSelected ? "is-selected" : ""
                    }`.trim()}
                    aria-hidden="true"
                  >
                    {isSelected ? <FiCheck /> : null}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="email-first-role-selector-empty">
              No matching access found.
            </div>
          )}
        </div>
        <div className="email-first-role-selector-note">
          <span
            className={`email-first-role-selector-note__dot ${
              hasSelectedVisibleRole ? "is-active" : ""
            }`.trim()}
            aria-hidden="true"
          />
          <span>{getRoleSelectionMessage(selectedRole)}</span>
        </div>
        <div
          className="email-first-role-selector-actions email-first-role-selector-actions--responsive"
          data-testid="role-picker-actions"
        >
          <button
            type="button"
            className="email-first-ghost-btn"
            disabled={isLoading}
            onClick={resetStepState}
          >
            Use a different email address
          </button>
          <button
            id="login"
            type="button"
            className="l-btn email-first-role-selector-continue email-first-role-selector-continue--responsive"
            disabled={isLoading || !hasSelectedVisibleRole}
            onClick={() => void handleContinueWithSelectedRole()}
          >
            Continue
          </button>
        </div>
      </>
    );
  };

  if (isCheckingLogin) {
    return <div>Loading...</div>;
  }

  if (step === "mfa") {
    if (mfaMode === "legacy" && mfaUsername) {
      return (
        <MfaLogin
          mode="legacy"
          username={mfaUsername}
          userId={mfaUserId || undefined}
          channels={channels}
          onBackToLogin={resetStepState}
        />
      );
    }

    if (mfaMode === "session") {
      return (
        <MfaLogin
          mode="session"
          channels={channels}
          onSendOtp={sendEmailFirstOtp}
          onVerifyOtp={verifyEmailFirstOtp}
          onBackToLogin={resetStepState}
        />
      );
    }
  }

  return (
    <section className="login-wrap">
      <div className="login-cell">
        <div
          className={`login-block email-first-block ${
            step === "role-picker" ? "email-first-block--role-picker" : ""
          }`.trim()}
          data-testid="login-block"
        >
          <div className="l-logo-block text-center email-first-logo-wrap">
            <img src={Logo} alt="Docuverus" title="Docuverus" />
          </div>

          <div
            className={`auth-form ${
              step === "password" ? "mr-t30" : "email-first-auth-form"
            }`}
            data-testid="login-auth-form"
          >
            <div
              className={`email-first-panel ${
                step === "role-picker" ? "email-first-panel--role-picker" : ""
              }`.trim()}
            >
              {step === "email" ? renderInitialStep() : null}
              {step === "password" ? renderPasswordStep() : null}
              {step === "otp-choice" ? renderOtpChoiceStep() : null}
              {step === "otp-verify" ? renderOtpVerifyStep() : null}
              {step === "role-picker" ? renderRolePickerStep() : null}
            </div>
          </div>

          <div className="l-foot-links mr-t20 email-first-footer">
            <ul className="mx-auto">
              <li>
                <a href="/atss/DataPolicy" target="_blank" rel="noreferrer">
                  Data Policy
                </a>
              </li>

              <li>
                <a href="/atss/TermsOfUse" target="_blank" rel="noreferrer">
                  Terms of Use
                </a>
              </li>
              <li>Version {SOFTWARE_VERSION}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
